import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { InfomodalComponent } from '../infomodal/infomodal.component';

@Component({
  selector: 'app-oclink',
  templateUrl: './oclink.component.html',
  styleUrls: ['./oclink.component.scss'],
  standalone: false
})
export class OclinkComponent implements OnInit {

  @Input() ocItem: any;
  @Input() allOcItems: any[] = [];
  @Input() userData: any;

  // OC Items Selection State (Multi-OC Items grouping)
  selectableOcItems: any[] = [];
  showOcItemsSelector: boolean = false;

  // Cotizaciones State
  cotizacionesList: any[] = [];
  filteredCotizaciones: any[] = [];
  searchQuery: string = '';
  selectedCotizacionId: number = 0;
  onlyMatchAmount: boolean = true;

  // Items State
  linkType: 'general' | 'specific' = 'general';
  cotizacionItems: any[] = [];

  loadingCotizaciones: boolean = false;
  loadingItems: boolean = false;
  saving: boolean = false;
  Math = Math; // Expose Math to template

  // Wizard
  currentStep: number = 1;
  selectedCotizacion: any = null;
  isAlreadyLinked: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosUi: Metodosui,
    private metodosGenService: MetodosgenService
  ) { }

  ngOnInit() {
    this.initSelectableOcItems();
    this.checkInitialLink();
    this.loadCotizaciones();
  }

  initSelectableOcItems() {
    const sourceList = (this.allOcItems && this.allOcItems.length > 0) ? this.allOcItems : (this.ocItem ? [this.ocItem] : []);
    this.selectableOcItems = sourceList.map(item => ({
      ...item,
      selected: String(item.id) === String(this.ocItem?.id),
      isCurrent: String(item.id) === String(this.ocItem?.id)
    }));
  }

  get totalOcSelectedAmount(): number {
    const selected = this.selectableOcItems.filter(i => i.selected);
    if (selected.length === 0) return parseFloat(this.ocItem?.importe_neto) || 0;
    return selected.reduce((sum, i) => sum + (parseFloat(i.importe_neto) || 0), 0);
  }

  get selectedOcItemsCount(): number {
    return this.selectableOcItems.filter(i => i.selected).length || 1;
  }

  get hasMultipleOcItems(): boolean {
    return this.selectableOcItems.length > 1;
  }

  get selectedOcItemsList(): any[] {
    const list = this.selectableOcItems.filter(i => i.selected);
    return list.length > 0 ? list : [this.ocItem];
  }

  toggleOcItemSelection(item: any, event?: Event) {
    if (event) event.stopPropagation();

    // El ítem origen desde el que se abrió el modal es obligatorio y no se puede desmarcar
    if (item.isCurrent) {
      return;
    }

    item.selected = !item.selected;
    this.recalculateMatchingItems();
  }

  recalculateMatchingItems() {
    const targetAmount = this.totalOcSelectedAmount;
    this.cotizacionesList.forEach(c => {
      const parts = (c.rawDt15 || '').split('|');
      const itemPrices = (parts[6] || '').split(',').map((p: string) => parseFloat(p)).filter((p: number) => !isNaN(p));
      c.hasMatchingItem = (Math.abs(c.subtotal - targetAmount) <= 0.01) || 
                          itemPrices.some((price: number) => Math.abs(price - targetAmount) <= 0.01);
    });
    this.applyFilters();
  }

  checkInitialLink() {
    if (this.ocItem?.vinculaciones && this.ocItem.vinculaciones.length > 0) {
      this.isAlreadyLinked = true;
      this.selectedCotizacionId = Number(this.ocItem.vinculaciones[0].cotizacion_id);
    } else {
      this.isAlreadyLinked = false;
    }
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async loadCotizaciones() {
    this.loadingCotizaciones = true;
    try {
      const payload = this.metodosGenService.setModeloGeneral(
        'cotizaciones', 'cotizacionesAll', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      );
      const data: any = await firstValueFrom(this.javapiService.getCommonModel(payload));

      if (Array.isArray(data)) {
        const currentTargetAmount = this.totalOcSelectedAmount;

        this.cotizacionesList = data.filter(c => {
          const cliente = (c.dt3 || '').toLowerCase();
          return cliente.includes('don antonio');
        }).map(c => {
          const rawDt15 = c.dt15 || '';
          const parts = rawDt15.split('|');
          const linkedCount = parseInt(parts[1]) || 0;
          const totalItems = parseInt(parts[2]) || 0;
          const ocNumbers = parts[3] || '';
          const isGeneralLinked = parts[4] === '1';
          const isInvoiced = parts[5] === '1';
          const itemPrices = (parts[6] || '').split(',').map((p: string) => parseFloat(p)).filter((p: number) => !isNaN(p));

          // Status logic
          const isFullyLinked = isGeneralLinked || (totalItems > 0 && linkedCount >= totalItems);
          const isDisabled = isFullyLinked; // Se bloquea solo si ya está vinculada. Si está facturada pero sin OC, se permite vincular.

          let statusMessage = '';
          if (isInvoiced) {
            statusMessage = 'Facturada';
          } else if (isFullyLinked) {
            statusMessage = `Ya vinculado con OC #${ocNumbers || '?'}`;
          } else if (linkedCount > 0) {
            statusMessage = `${totalItems - linkedCount} de ${totalItems} ítems no vinculados`;
          }

          // Amount matching logic: Check if subtotal or individual item prices match the target net import
          const hasMatchingItem = (Math.abs(parseFloat(c.dt4) - currentTargetAmount) <= 0.01) || 
                                  itemPrices.some((price: number) => Math.abs(price - currentTargetAmount) <= 0.01);

          // Priority logic (Not linked, not invoiced)
          const isPriority = linkedCount === 0 && !isInvoiced;

          return {
            id: Number(c.dt1),
            cliente: c.dt3,
            direccion: c.dt6,
            subtotal: parseFloat(c.dt4) || 0,
            descripcion: c.dt7,
            estado: c.dt5,
            total: parseFloat(c.dt9) || 0,
            rawDt15,
            // Link info
            isFullyLinked,
            isInvoiced,
            isDisabled,
            isPriority,
            hasMatchingItem,
            ocNumbers,
            statusMessage,
            totalItems
          };
        });

        // Sorting: Priority first, then by ID desc
        this.cotizacionesList.sort((a, b) => {
          if (a.isPriority && !b.isPriority) return -1;
          if (!a.isPriority && b.isPriority) return 1;
          return b.id - a.id;
        });

        if (this.isAlreadyLinked && this.selectedCotizacionId) {
          this.filteredCotizaciones = this.cotizacionesList.filter(c => c.id === this.selectedCotizacionId);
          this.selectedCotizacion = this.filteredCotizaciones[0];
        } else {
          this.applyFilters();
        }
      }
    } catch (e) {
      console.error('Error fetching cotizaciones:', e);
      this.metodosUi.showToast('Error al cargar cotizaciones', 'danger');
    } finally {
      this.loadingCotizaciones = false;
    }
  }

  applyFilters() {
    let list = [...this.cotizacionesList];

    // Search query filter
    if (this.searchQuery.trim() !== '') {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(c =>
        c.id.toString().includes(q) ||
        (c.cliente && c.cliente.toLowerCase().includes(q)) ||
        (c.direccion && c.direccion.toLowerCase().includes(q)) ||
        (c.descripcion && c.descripcion.toLowerCase().includes(q))
      );
    }

    // Amount match filter
    if (this.onlyMatchAmount) {
      list = list.filter(c => c.hasMatchingItem);
    }

    this.filteredCotizaciones = list;
  }

  toggleAmountFilter() {
    this.onlyMatchAmount = !this.onlyMatchAmount;
    this.applyFilters();
  }

  async desvincular() {
    const confirm = await this.metodosUi.showConfirmation(
      'Desvincular OC',
      `¿Estás seguro que deseas desvincular esta OC de la cotización #${this.selectedCotizacionId}? Se listarán las demás cotizaciones para una nueva vinculación.`
    );

    if (!confirm) return;

    this.saving = true;
    try {
      const usuario = this.metodosGenService.getUsuario();
      const payload = this.metodosGenService.setModeloGeneral(
        "cotizaciones", "linkOcCotizacion",
        String(this.ocItem.id), "0", "0", usuario, "N",
        '', '', '', '', '', '', '', '', '', ''
      );
      await firstValueFrom(this.javapiService.getCommonModel(payload));
      this.metodosUi.showToast('Desvinculado correctamente', 'success');
      this.modalCtrl.dismiss({ success: true });
      this.saving = false;
    } catch (e) {
      console.error('Error unlinking:', e);
      this.metodosUi.showToast('Error al desvincular', 'danger');
      this.saving = false;
    }
  }

  searchCotizacion(event: any) {
    this.searchQuery = (event.target?.value || '').toLowerCase();
    this.applyFilters();
  }

  async onCotizacionSelected() {
    if (!this.selectedCotizacionId) return;

    this.loadingItems = true;
    this.cotizacionItems = [];

    try {
      const payload = this.metodosGenService.setModeloGeneral(
        "cotizaciones", "cotizacionGet", String(this.selectedCotizacionId), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      );
      const data: any = await firstValueFrom(this.javapiService.getCommonModel(payload));

      if (Array.isArray(data) && data.length > 0) {
        this.cotizacionItems = data.filter(d => d.dt1 !== 'HEADER').map(c => ({
          id: Number(c.dt1) || 0,
          descripcion: c.dt2,
          cantidad: Number(c.dt3) || 0,
          precio: parseFloat(c.dt6) || 0,
          selected: false
        }));
      }
    } catch (e) {
      console.error('Error fetching cotizacion details:', e);
      this.metodosUi.showToast('Error al cargar detalle de cotización', 'danger');
    } finally {
      this.loadingItems = false;
    }
  }

  nextStep() {
    if (this.currentStep === 1) {
      if (!this.selectedCotizacionId) {
        this.metodosUi.showToast('Selecciona una cotización primero', 'warning');
        return;
      }
      this.selectedCotizacion = this.cotizacionesList.find(c => c.id === this.selectedCotizacionId);
      this.onCotizacionSelected();
    }
    this.currentStep++;
  }

  async previewCotizacion(evt: Event, cot: any) {
    evt.stopPropagation();
    
    const loading = await this.metodosUi.showLoading('Cargando detalle...');
    
    try {
      const payload = this.metodosGenService.setModeloGeneral(
        "cotizaciones", "cotizacionGet", String(cot.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      );
      const data: any = await firstValueFrom(this.javapiService.getCommonModel(payload));
      
      let modalItems: any[] = [];
      if (Array.isArray(data) && data.length > 0) {
        const items = data.filter(d => d.dt1 !== 'HEADER');
        
        let counter = 1;
        for (let item of items) {
          modalItems.push({
            label: `Ítem #${counter}`,
            value: `${item.dt2}\nCant: ${item.dt3} | P.U: $${parseFloat(item.dt6 || 0).toFixed(2)}`,
            icon: 'cube-outline'
          });
          counter++;
        }
      }

      if (modalItems.length === 0) {
        modalItems.push({ label: 'Ítems', value: 'Sin ítems registrados' });
      }

      const modal = await this.modalCtrl.create({
        component: InfomodalComponent,
        cssClass: 'premium-modal',
        componentProps: {
          title: `Cotización #${cot.id}`,
          subtitle: cot.cliente,
          sections: [
            {
              title: 'Información General',
              items: [
                { label: 'Estado', value: cot.estado, icon: 'analytics-outline' },
                { label: 'Subtotal', value: `$${cot.subtotal.toFixed(2)}`, icon: 'cash-outline' },
                { label: 'Total', value: `$${cot.total.toFixed(2)}`, icon: 'cash-outline' },
                { label: 'Dirección', value: cot.direccion || 'N/A', icon: 'location-outline' },
                { label: 'Descripción', value: cot.descripcion || 'Sin descripción', icon: 'document-text-outline' }
              ]
            },
            {
              title: 'Detalle de Ítems',
              items: modalItems
            }
          ]
        }
      });

      await modal.present();

    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error al cargar detalle', 'danger');
    } finally {
      this.metodosUi.hideLoading();
    }
  }

  prevStep() {
    this.currentStep--;
  }

  async registerMemo(evt: Event, cot: any) {
    evt.stopPropagation();
    
    const confirm = await this.metodosUi.showConfirmation('Registrar Nota', `¿Deseas registrar una nota en este ítem indicando que corresponde a la cotización #${cot.id} (actualmente relacionada a OC ${cot.ocNumbers})?`);
    if (!confirm) return;

    this.saving = true;
    const memoText = `Cot: ${cot.id} (OC: ${cot.ocNumbers})`;

    try {
      const payload = this.metodosGenService.setModeloGeneral(
        "oc_smrey", "addMemo", String(this.ocItem.id), memoText, String(this.userData?.l_usuario || ''), '', '', '', '', '', '', '', '', '', '', '', ''
      );
      await firstValueFrom(this.javapiService.getOcModel(payload));
      this.metodosUi.showToast('Nota registrada con éxito', 'success');
      this.modalCtrl.dismiss({ success: true });
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error al registrar nota', 'danger');
    } finally {
      this.saving = false;
    }
  }

  get selectedLinkTotal(): number {
    if (this.linkType === 'general') {
      return this.selectedCotizacion?.subtotal || 0; // Use subtotal (Net Price)
    } else {
      return this.cotizacionItems
        .filter(i => i.selected)
        .reduce((sum, i) => sum + (i.precio * i.cantidad), 0);
    }
  }

  get isValidAmount(): boolean {
    if (!this.selectedCotizacionId) return false;
    const margin = 0.01;
    const diff = Math.abs(this.selectedLinkTotal - this.totalOcSelectedAmount);
    return diff <= margin;
  }

  get amountDiffMessage(): string {
    const diff = this.selectedLinkTotal - this.totalOcSelectedAmount;
    if (Math.abs(diff) <= 0.01) return '';
    return diff > 0 ? 'exceso' : 'faltante';
  }

  get amountDiffValue(): number {
    return Math.abs(this.selectedLinkTotal - this.totalOcSelectedAmount);
  }

  async saveLink() {
    if (!this.selectedCotizacionId) {
      this.metodosUi.showToast('Selecciona una cotización', 'warning');
      return;
    }

    const ocItemsToLink = this.selectedOcItemsList;
    if (ocItemsToLink.length === 0) {
      this.metodosUi.showToast('No hay ítems de OC seleccionados', 'warning');
      return;
    }

    this.saving = true;

    try {
      const usuario = this.metodosGenService.getUsuario();

      for (const ocItm of ocItemsToLink) {
        if (this.linkType === 'general') {
          const payload = this.metodosGenService.setModeloGeneral(
            "cotizaciones", "linkOcCotizacion",
            String(ocItm.id), String(this.selectedCotizacionId), "0", usuario, "N",
            '', '', '', '', '', '', '', '', '', ''
          );
          await firstValueFrom(this.javapiService.getCommonModel(payload));
        } else {
          const selectedItems = this.cotizacionItems.filter(i => i.selected);
          if (selectedItems.length === 0) {
            this.metodosUi.showToast('Selecciona al menos un ítem específico de la cotización', 'warning');
            this.saving = false;
            return;
          }

          // Para este ítem de la OC, asociamos los ítems de cotización seleccionados
          for (let i = 0; i < selectedItems.length; i++) {
            const item = selectedItems[i];
            const isAppend = i > 0 ? "Y" : "N"; // Primera llamada borra anteriores del oc_item, las siguientes anexan
            const payload = this.metodosGenService.setModeloGeneral(
              "cotizaciones", "linkOcCotizacion",
              String(ocItm.id), String(this.selectedCotizacionId), String(item.id), usuario, isAppend,
              '', '', '', '', '', '', '', '', '', ''
            );
            await firstValueFrom(this.javapiService.getCommonModel(payload));
          }
        }
      }

      this.saving = false;
      this.modalCtrl.dismiss({ success: true });
    } catch (e) {
      console.error('Error in linking:', e);
      this.metodosUi.showToast('Error al guardar la vinculación', 'danger');
      this.saving = false;
    }
  }
}
